"use client";

import { Modal, Image, Box, Button, Progress, Text, Flex } from "@mantine/core";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { useMultipartUpload } from "../useMultipartUpload";
import { useAppDispatch } from "@/lib/hooks";
import { contentTableApi } from "@/features/ContentTable/ContentTable.api";
import { contentApi } from "@/features/Content/Content.api";

const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

const formatDuration = (seconds: number) => {
  if (seconds === Infinity || isNaN(seconds)) return "calculating...";
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
};

interface MediaPreviewModalProps {
  opened: boolean;
  onClose: () => void;
  file: File | null;
}

export function MediaPreviewModal({
  opened,
  onClose,
  file,
}: MediaPreviewModalProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const previewUrl = useMemo(
    () => (file ? URL.createObjectURL(file) : null),
    [file],
  );

  const { uploadFile, isUploading, progress, reset, abortUpload, uploadRef } =
    useMultipartUpload({
      onSuccess: (location, contentId) => {
        notifications.show({
          title: "Upload Complete",
          message: "Your file has been uploaded successfully!",
          color: "green",
        });
        onClose();
        dispatch(contentTableApi.util.invalidateTags(["ContentTable"]));
        dispatch(contentApi.util.invalidateTags(["Content"]));
        if (contentId) {
          navigate(`/contents/${contentId}`);
        } else {
          navigate("/contents");
        }
      },
      onError: (error) => {
        notifications.show({
          title: "Upload Failed",
          message: error.message || "Something went wrong during upload.",
          color: "red",
        });
      },
    });

  // Warn user and abort upload if they try to close/refresh the page
  useEffect(() => {
    if (!isUploading) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };

    const handleUnload = () => {
      if (uploadRef.current) {
        const payload = JSON.stringify(uploadRef.current);
        navigator.sendBeacon(
          "/api/v1/assets/presigned-url/multipart/abort",
          new Blob([payload], { type: "application/json" }),
        );
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("unload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("unload", handleUnload);
    };
  }, [isUploading, uploadRef]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Reset upload state when modal closes
  useEffect(() => {
    if (!opened) {
      reset();
    }
  }, [opened, reset]);

  if (!file || !previewUrl) return null;

  const isVideo = file.type.startsWith("video/");

  const handleUpload = async () => {
    if (!file) return;
    await uploadFile(file);
  };

  const handleClose = () => {
    if (!isUploading) {
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={file.name}
      size="lg"
      centered
      closeOnClickOutside={!isUploading}
      closeOnEscape={!isUploading}
    >
      <Flex justify="center">
        {isVideo ? (
          <video
            src={previewUrl}
            controls
            style={{
              width: "100%",
              maxWidth: "640px",
              height: "auto",
              maxHeight: "360px",
              borderRadius: "8px",
            }}
          />
        ) : (
          <Image src={previewUrl} radius="md" alt="Preview" />
        )}
      </Flex>

      {isUploading && progress && (
        <Box mt="md">
          <Flex justify="space-between" align="center" mb="xs">
            <Text size="sm" fw={500}>
              Uploading...
            </Text>
            <Text size="xs" c="dimmed">
              {progress.eta > 0
                ? `Est. remaining: ${formatDuration(progress.eta)}`
                : progress.loaded > 0
                  ? "Almost done..."
                  : "Calculating remaining time..."}
            </Text>
          </Flex>
          <Progress
            value={progress.percentage}
            size="lg"
            radius="xl"
            striped
            animated
          />
          <Flex justify="space-between" mt="xs" align="center">
            <Text size="xs" c="dimmed">
              {progress.percentage}% completed{" "}
              {progress.speed > 0 && `(at ${formatBytes(progress.speed)}/s)`}
            </Text>
            <Text size="xs" c="dimmed">
              Uploaded: {formatBytes(progress.loaded)} of{" "}
              {formatBytes(progress.total)} (
              {formatBytes(progress.total - progress.loaded)} remaining)
            </Text>
          </Flex>
        </Box>
      )}

      <Flex justify="flex-end" mt="xl" gap={"sm"}>
        <Button variant="default" onClick={handleClose} disabled={isUploading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          loading={isUploading}
          disabled={isUploading}
        >
          Start Upload
        </Button>
      </Flex>
    </Modal>
  );
}
